import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, ButtonBase, Chip, Drawer, Stack, Tooltip, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import MenuCard from './MenuCard';
import { drawerWidth, miniDrawerWidth } from 'store/constant';
import { adminTokens } from 'ui-component/admin/AdminPage';
import { IconMenu2 } from '@tabler/icons-react';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {
  const theme = useTheme();
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const isMini = matchUpMd && !drawerOpen;

  const mobileHeader = (
    <Box
      sx={{
        height: 64,
        px: 1.5,
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${adminTokens.border}`,
        bgcolor: adminTokens.surface
      }}
    >
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <LogoSection />
      </Box>
      <Tooltip title="收起菜单" placement="bottom" arrow>
        <ButtonBase aria-label="收起菜单" sx={{ borderRadius: '6px', overflow: 'hidden' }} onClick={drawerToggle}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              width: 36,
              height: 36,
              color: adminTokens.accent,
              background: adminTokens.hover,
              '&:hover': { background: '#DDEDE8', color: adminTokens.accentHover }
            }}
            color="inherit"
          >
            <IconMenu2 stroke={1.5} size="1.3rem" />
          </Avatar>
        </ButtonBase>
      </Tooltip>
    </Box>
  );

  const drawer = (
    <>
      {mobileHeader}
      <BrowserView style={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <PerfectScrollbar
              component="div"
              style={{
                height: '100%',
                paddingLeft: isMini ? '8px' : '10px',
                paddingRight: isMini ? '8px' : '10px',
                paddingTop: '10px',
                paddingBottom: '10px'
              }}
            >
              <MenuList />
              {process.env.REACT_APP_VERSION && (
                <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
                  <Chip label={process.env.REACT_APP_VERSION} disabled size="small" />
                </Stack>
              )}
            </PerfectScrollbar>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              px: isMini ? 1 : 1.25,
              py: 1,
              borderTop: `1px solid ${adminTokens.border}`,
              bgcolor: adminTokens.surface
            }}
          >
            <MenuCard collapsed={isMini} footer />
          </Box>
        </Box>
      </BrowserView>
      <MobileView>
        <Box sx={{ px: 1.5, py: 1 }}>
          <MenuList />
          <MenuCard />
          {process.env.REACT_APP_VERSION && (
            <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
              <Chip label={process.env.REACT_APP_VERSION} disabled size="small" />
            </Stack>
          )}
        </Box>
      </MobileView>
    </>
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        width: { xs: 0, md: drawerOpen ? drawerWidth : miniDrawerWidth },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.shorter
        })
      }}
      aria-label="侧边导航"
    >
      <Drawer
        container={container}
        variant={matchUpMd ? 'persistent' : 'temporary'}
        anchor="left"
        open={matchUpMd ? true : drawerOpen}
        onClose={drawerToggle}
        sx={{
          pointerEvents: matchUpMd || drawerOpen ? 'auto' : 'none',
          '& .MuiDrawer-paper': {
            pointerEvents: 'auto',
            width: 'min(280px, calc(100vw - 48px))',
            maxWidth: '100vw',
            background: adminTokens.surface,
            color: adminTokens.ink,
            borderRight: `1px solid ${adminTokens.border}`,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.shorter
            }),
            [theme.breakpoints.up('md')]: {
              top: 72,
              width: drawerOpen ? drawerWidth : miniDrawerWidth,
              height: 'calc(100vh - 72px)'
            }
          }
        }}
        ModalProps={{ keepMounted: false }}
        color="inherit"
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

Sidebar.propTypes = {
  drawerOpen: PropTypes.bool,
  drawerToggle: PropTypes.func,
  window: PropTypes.object
};

export default Sidebar;
